import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Avatar,
    Divider,
    Link,
    Tooltip,
    CircularProgress,
    Alert,
    Stack,
    Skeleton,
    ThemeProvider,
    createTheme,
    CssBaseline,
    Button,
    ButtonGroup,
    Container,
    Paper,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { transformToBTC } from '../../utils/helpers';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
// --- UPDATED PRICE CHART COMPONENT ---
const TokenPriceChart = ({ tokenId }) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [xDomain, setXDomain] = useState(['dataMin', 'dataMax']);
    const [isZoomed, setIsZoomed] = useState(false);
    const [selectedResolution, setSelectedResolution] = useState(240); // Default to 4 hours
    const theme = useTheme();
    const { price: btcPrice } = useBitcoinPrice();

    const resolutionOptions = [
        { label: '1m', value: 1 },
        { label: '15m', value: 15 },
        { label: '1h', value: 60 },
        { label: '4h', value: 240 },
        { label: '12h', value: 720 }
    ];



    const chartRef = useRef(null);
    const panState = useRef({ isPanning: false, startX: 0, startDomain: [0, 0] });

    const fetchChartData = useCallback(async (to, from) => {
        if (!tokenId) return [];
        try {
            const url = `https://api.odin.fun/v1/token/${ tokenId }/tv_feed?resolution=${ selectedResolution }&from=${ from }&to=${ to }`;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Chart data fetch failed: ${ response.status }`);
            const result = await response.json();
            return result.map(d => ({ ...d, time: d.time * 1000 }));
        } catch (err) {
            setError(err.message);
            return [];
        }
    }, [tokenId, selectedResolution]);

    const loadMoreData = useCallback(async () => {
        if (isFetchingMore || !hasMoreData || chartData.length === 0) return;
        setIsFetchingMore(true);
        const oldestTimestamp = chartData[0].time;
        const from = new Date(oldestTimestamp);
        from.setDate(from.getDate() - 180);
        const olderData = await fetchChartData(oldestTimestamp, from.getTime());
        if (olderData.length > 0) {
            const allData = [...olderData, ...chartData];
            const uniqueData = Array.from(new Map(allData.map(item => [item.time, item])).values());
            uniqueData.sort((a, b) => a.time - b.time);
            setChartData(uniqueData);
        } else {
            setHasMoreData(false);
        }
        setIsFetchingMore(false);
    }, [isFetchingMore, hasMoreData, chartData, fetchChartData]);

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            const to = new Date().getTime();
            const from = new Date();
            from.setDate(from.getDate() - 180);
            const initialData = await fetchChartData(to, from.getTime());
            setChartData(initialData);
            setXDomain(['dataMin', 'dataMax']);
            setIsZoomed(false);
            setHasMoreData(true);
            setLoading(false);
        };
        if (tokenId) {
            loadInitialData();
        }
    }, [tokenId, selectedResolution, fetchChartData]);

    const handleMouseDown = (e) => {
        if (e) {
            panState.current = { isPanning: true, startX: e.clientX, startDomain: xDomain };
            if (chartRef.current) chartRef.current.style.cursor = 'grabbing';
        }
    };

    const handleMouseMove = (e) => {
        if (panState.current?.isPanning && e) {
            const dx = e.clientX - panState.current.startX;
            const domainWidth = panState.current.startDomain[1] - panState.current.startDomain[0];
            const chartWidth = chartRef.current?.getBoundingClientRect().width || 1;
            const timeDelta = (dx / chartWidth) * domainWidth;

            const newDomain = [panState.current.startDomain[0] - timeDelta, panState.current.startDomain[1] - timeDelta];

            if (newDomain[0] < chartData[0]?.time) {
                loadMoreData();
            }

            setXDomain(newDomain);
            setIsZoomed(true);
        }
    };

    const handleMouseUpOrLeave = () => {
        if (panState.current) panState.current.isPanning = false;
        if (chartRef.current) chartRef.current.style.cursor = 'grab';
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const chart = chartRef.current?.querySelector('.recharts-wrapper');
        if (chart && e.nativeEvent) {
            const rect = chart.getBoundingClientRect();
            const mouseX = e.nativeEvent.clientX - rect.left;

            const [min, max] = xDomain[0] === 'dataMin' ? [chartData[0].time, chartData[chartData.length - 1].time] : xDomain;
            const range = max - min;
            const cursorTime = min + (range * (mouseX / rect.width));

            const zoomFactor = e.nativeEvent.deltaY < 0 ? 0.8 : 1.2;
            const newMin = cursorTime - (cursorTime - min) * zoomFactor;
            const newMax = cursorTime + (max - cursorTime) * zoomFactor;

            setXDomain([newMin, newMax]);
            setIsZoomed(true);
        }
    };

    const resetZoom = () => {
        setXDomain(['dataMin', 'dataMax']);
        setIsZoomed(false);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const price = payload[0].value;
            const btcValue = transformToBTC(price);
            const usdValue = ((price / 1e11) * btcPrice);
            return (
                <Paper sx={{ p: 2, background: 'rgba(0,0,0,0.8)' }}>
                    <Typography variant="body2">{new Date(label).toLocaleString()}</Typography>
                    <Typography variant="body1" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                        {btcValue}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'grey.400' }}>
                        ${usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                    </Typography>
                </Paper>
            );
        }
        return null;
    };

    return (
        <Paper sx={{ mt: 4, p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <ButtonGroup size="small" variant="outlined">
                    {resolutionOptions.map((option) => (
                        <Button
                            key={option.value}
                            onClick={() => setSelectedResolution(option.value)}
                            variant={selectedResolution === option.value ? 'contained' : 'outlined'}
                            sx={{
                                minWidth: '50px',
                                backgroundColor: selectedResolution === option.value ? theme.palette.secondary.main : 'transparent',
                                color: selectedResolution === option.value ? 'white' : theme.palette.secondary.main,
                                '&:hover': {
                                    backgroundColor: selectedResolution === option.value ? theme.palette.secondary.main : theme.palette.secondary.main,
                                }
                            }}
                        >
                            {option.label}
                        </Button>
                    ))}
                </ButtonGroup>
                {isZoomed && <Button onClick={resetZoom} size="small">Reset Zoom</Button>}
            </Box>
            <Box ref={chartRef} sx={{ position: 'relative', minHeight: 350, cursor: 'grab' }} onWheel={handleWheel} >
                {isFetchingMore && <CircularProgress size={24} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }} />}
                {loading ? <Skeleton variant="rectangular" height={350} /> : error ? <Alert severity="error">{error}</Alert> :
                    <ResponsiveContainer width="100%" height={350}>
                        <AreaChart
                            data={chartData}
                            margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUpOrLeave}
                            onMouseLeave={handleMouseUpOrLeave}
                        >
                            <defs><linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.8} /><stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis dataKey="time" stroke="grey" tickFormatter={(unixTime) => new Date(unixTime).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })} scale="time" type="number" domain={xDomain} allowDataOverflow={true} />
                            <YAxis
                                stroke="grey"
                                tickFormatter={(price) => {
                                    const btcValue = transformToBTC(price);
                                    const usdValue = ((price / 1e11) * btcPrice);
                                    const formattedUSD = usdValue < 0.01 ? usdValue.toFixed(4) : usdValue.toFixed(2);
                                    return `${ btcValue }\n$${ formattedUSD }`;
                                }}
                                domain={['auto', 'auto']}
                                allowDataOverflow
                                width={80}
                                tick={{ fontSize: 10 }}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="close" stroke={theme.palette.secondary.main} strokeWidth={2} fillOpacity={1} fill="url(#colorUv)" />
                        </AreaChart>
                    </ResponsiveContainer>
                }
            </Box>
        </Paper>
    );
};

export default TokenPriceChart;